import * as flights from '../flights';

export * from './AccountService';
export * from './BannerService';
export * from './BestPriceService';
export * from './ContentService';
export * from './CouponService';
export * from './DestinationService';
export * from './FlightService';
export * from './PromotionService';
export * from './ReservationService';
export * from './RetailRocket';
export * from './SupportService';
export * from './UtilsService';

export interface Aircraft {
    uniqueId: string;
    iataCode: string;
    manufacturer: string;
    model: string;
    displayName: string;
}

export interface Airline {
    uniqueId: string;
    iataCode: string;
    name: string;
    shortName: string;
    alliance: string;
}

export interface Animals {
    type: string;
    weight: number;
    containerDimensions: Dimensions;
}

export interface ApiRequest {
    currencyCode?: string;
    locale?: string;
    theme?: string;
    host?: string;
    instance?: string;
    marketCode?: string;
}

export enum BaggageChoiceType {
    notSet,
    manualAccepted,
    manualDenied,
    confirmationAccepted,
    confirmationDenied
}

export interface Banner {
    id: number;
    content: string;
}

export interface BannerRequest extends ApiRequest {
    displayArea: string;
    departureDestinationId?: number;
    arrivalDestinationId?: number;
    validatingAirlineId?: number;
}

export interface BillInfo {
    id: number;
    paymentTypeId: string;
    amount: number;
    billStatus: string;
    billType: string;
    billTypeName: string;
    paymentError: string;
    publicId: string;
}

export interface BonusStatus {
    creditTotal: number;
    debitTotal: number;
    currentBalance: number;
    canBeUsed: boolean;
    leftToFirstUse: number;
    transfers: BonusTransfer[];
}

export interface BonusTransfer {
    id: number;
    userId: number;
    bonusAmount: number;
    transferType: string;
    transferReason: string;
    uiReason: string;
    date: string;
    reservation: ReservationListModel;
}

export interface BookProductsRequest extends ApiRequest {
    parentReservationPublicId: string;
    paymentTypeId: string;
    productIds: number[];
    confirmationPageUrlTemplate: string;
    gaClientID: string;
    creditCard?: CreditCard;
    bonusAmount?: number;
    clientDevice: ClientDevice;
    choiceType: ProductChoiceType;
    redirectDomain?: string;
}

export interface BookProductsResponse {
    reservationPublicId: string;
    parentReservationPublicId: string;
    paymentInfo: PaymentInfo;
}

export interface CancelProductRequest extends ApiRequest {
    productReservationId: number;
}

export interface CancelProductAfterPopupRequest extends CancelProductRequest {
    applyDiscount: boolean;
}

export interface CheckFlightRequest extends ApiRequest {
    searchId: string;
    flightId: string;
    bookingSessionId?: number;
}

export interface CheckFlightResponse {
    flight: Flight;
}

export interface CheckFlightConfirmRequest extends ApiRequest {
    bookingSessionId: number;
    elapsedMsClient: number;
    flightId: string;
    searchId: string;
}

export interface ClientDevice {
    browser: string;
    colorDepth: number;
    ipAddress: string;
    javaEnabled: boolean;
    language: string;
    platform: string;
    screenHeight: number;
    screenWidth: number;
    timezone: number;
    type: string;
    userAgent: string;
    windowHeight: number;
    windowWidth: number;
}

export interface Coach {
    schemaId: string;
    number: string;
    type: string;
    isTwoStorey: boolean;
    isCanChooseBedding: boolean;
    hasDynamicPricing: boolean;
    isPetsAllowed: boolean;
    isPetInCoachAllowed: boolean;
    electronicTicket: boolean;
    isBranded: boolean;
    beddingTariff: null;
    loyaltyCards: string[] | null;
    fareInfo: null;
    places: CoachPlace[];
    placesInfo: {
        lowerSide: null;
        upperSide: null;
        upperCoupe: number;
        lowerCoupe: number;
        total: null;
    };
    serviceClass: {
        code: string;
        name: string;
        description: string;
    };
}

export interface CoachPlace {
    number: number;
    gender: 'None';
    placeType: 'Lower';
    fare: {
        amount: number;
        currency: string;
    };
}

/**
 * Coach schemaId according to coach *.svg file name.
 * Files located in Js/Core/coach-chemas.
 * */
export enum CoachTemplates {
    'FPK_2/4_V1' = 'FPK_2-4_V1',
    'FPK_3/4_V1' = 'FPK_3-4_V1'
}

export interface CorporateAccount {
    status: 'Blocked' | 'Active' | 'InProgress';
    currentBalance: number;
    overdraftLimit: number;
    creditTotal: number;
    debitTotal: number;
    canBeUsed: boolean;
}

export interface ContentText {
    id: string;
    body: string;
}

export interface ContentTextRequest extends ApiRequest {
    id: string;
}

export interface CouponCheckModel {
    id: number;
    guid: string;
    name: string;
    expiryDate: string; // string used because of timezone problems with Date
    discountAmount: number;
    discountAmountBase: number;
    productId: number;
    product?: any;
}

export interface CouponCheckRequest extends ApiRequest {
    code: string;
    searchId: string;
    email: string;
    flightId: string;
    partnerCookie: string;
    passengers: PassengerCreateData[];
}

export interface CouponListModel {
    guid: string;
    name: string;
    amount: number;
    expiryDate: string; // string used because of timezone problems with Date
    isUsed: boolean;
}

export interface CreateReservationRequest extends ApiRequest {
    paymentMethod?: any;
    adapterData?: string;
    animals?: Animals;
    baggageChoiceType: BaggageChoiceType;
    bonusAmount?: number;
    bookingSessionId: number;
    brandChoiceType: FlightBrandChoiceType;
    clientDevice: ClientDevice;
    confirmationPageUrlTemplate: string;
    couponGuid: string;
    creditCard?: CreditCard;
    flightId: string;
    gaClientID: string;
    markerReferrer: string | null;
    partnerCookie: string;
    passengersCreateData: PassengersCreateData;
    paymentTypeId: string;
    products: ProductBookingInfo[];
    searchId: string;
    timestamp: string;
    upgradedFromFlightId?: string;
    baggageOptions: string[];
    queryString: string;
}

export interface CreateReservationResult {
    paymentInfo: PaymentInfo;
    reservationInfo: ReservationInfo;
    authToken: string;
}

export interface StartedReservation{
    reservationPublicId: string;
}

export interface CreditCard {
    cardHolderName: string;
    cardNumber: string;
    cardCvv: string;
    cardExpMonth: number;
    cardExpYear: number; // Short format - YY
    encryptedPacket?: string;
}

export enum CustomBaggageFlightDirection {
    both = 'Both',
    outward = 'Outward',
    return = 'Return'
}

export interface CustomBaggage {
    bookable: boolean;
    options: CustomBaggageOption[] | null;
}

export interface CustomBaggageOption {
    baggageOptions: CustomBaggageItem[];
    customBaggageFlightDirection: CustomBaggageFlightDirection;
    destinationFromUID: string;
    destinationToUID: string;
}

export interface CustomBaggageItem {
    bagsQuantity: number;
    bagsWeight: string;
    currency: string;
    id: string;
    perPassenger: boolean;
    price: number;
    selected: boolean;
}

export interface CustomBaggageReservationDetails {
    options: CustomBaggageOptionReservationDetails[];
    totalPrice: number;
}

export interface CustomBaggageOptionReservationDetails {
    baggageOptions: CustomBaggageItemReservationDetails[];
    customBaggageFlightDirection: CustomBaggageFlightDirection;
    destinationFromUID: string;
    destinationToUID: string;
}

export interface CustomBaggageItemReservationDetails {
    bagsQuantity: number;
    bagsWeight: string;
    currency: string;
    passengerId: number;
    perPassenger: boolean;
    price: number;
}

export interface Destination {
    address: string | null;
    city: Destination;
    country: Destination;
    iataCode: string;
    localizedName: string;
    name: string;
    type: string;
    uniqueId: number;
}

export interface Dimensions {
    length: number;
    width: number;
    height: number;
}

export interface ExternalLinkRequest extends ApiRequest {
    searchId: string;
    flightId: string;
    externalPartnerId?: string;
    externalSearchId?: string;
    externalFlightId?: string;
    externalFlightBillingPrice?: string;
}

export interface ExternalLinkResponse {
    method: 'GET' | 'POST';
    url: string;
    pixelUrl: string;
    params?: { [key: string]: string };
}

export interface FareDetailsRequest extends ApiRequest {
    searchId: string;
    flightId: string;
    publicReservationId: string;
}

export interface FareDetailsResponse {
    isSuccess: boolean;
    agencyFareDetails?: string;
    gdsFareDetails?: string;
    gdsFareDetailsLocale?: string;
}

export interface FinishPreauth3dsRequest extends ApiRequest {
    reservationPublicId: string;
    billPublicId: string;
    paymentId: number;
}

export interface FlightBrand {
    brandGroupGuid: string;
    code: string;
    descriptionUI: string;
    descriptionUIShort: string;
    name: string;
    nameUI: string;
    url: string;
}

export interface Flight {
    adapter: string;
    adapterData: string; // Adapter specific data to pass to createOrRestartReservation
    area: string;
    basePrice: number;
    billingPrice: number;
    billingPricePerMonth: number;
    bookingClass: string;
    bookingChecked: boolean;
    bookingPossible: boolean;
    brand: FlightBrand;
    currencyCode?: string;
    customBaggage: CustomBaggage;
    exchangeRules: FlightModifyRules;
    externalOffers: FlightSearchExternalOffer[];
    externalPartner: FlightSearchPartner;
    farePrice: number;
    flightFlags: SegmentFlags[];
    flightKind: FlightKind;
    legs: Leg[];
    seatMaps: SeatMap[];
    multiFRType: number;
    priority: number;
    promo_coupon_name: string;
    refundRules: FlightModifyRules;
    regularBaggage: string;
    fee: number;
    taxes: number;
    tfid: number | null;
    uniqueId: string;
    validatingCarrierUID: string;
    additionalOfferKeys: string[];
}

export interface FlightBaggage {
    pieces: number;
    pieceWeight: number;
    length: number;
    width: number;
    height: number;
}

export enum FlightNotificationIcon {
    Info,
    Warn,
    Error
}

export interface FlightNotification {
    htmlText: string;
    icon: FlightNotificationIcon;
}

export interface SurchargeInfo {
    type: string;
    surchargeAmount: number;
    surchargeAmountCurrency: string;
    clientText: string;
    buttonAcceptText: string;
    buttonRejectText: string;
}

export interface FlightBookingDetails extends FlightDictionaries {
    partnerAirlines: Airline[];
    bookingSessionId: number;
    bookingPossible: boolean;
    surchargeInfo: SurchargeInfo;
    changeRefundFee: Price;
    constraints: ReservationConstraints;
    countryCrossing: string;
    countries: Destination[];
    defaultCitizenshipId: number;
    flights: Flight[];
    notifications: [];
    trains: Train[];
    trainCoaches: { [schemaId: string]: string };
    paymentTypes: PaymentTypeDesc[];
    products: Product[];
    productPopups: ProductPopup[];
    productPopupsDiscount: ProductPopup[];
    search: FlightSearchRequest;
    selectedFlightUniqueId: string;
    showPopups: boolean; // #4998
    prebookingTimeLimitMinutes?: number | null;
    brandedProductKits?: ProductBrand[];
    additionalOfferKeys: string[];
}

export enum FlightBrandChoiceType {
    notSet,
    manualDefault,
    manualUpgrade,
    confirmationDefault,
    confirmationUpgrade
}

export interface FlightDictionaries {
    aircrafts: { [uniqueId: string]: Aircraft };
    airlines: { [uniqueId: string]: Airline };
    destinations: { [uniqueId: string]: Destination };
}

export enum FlightKind {
    regular = 'Regular',
    charter = 'Charter',
    lowcost = 'Lowcost',
    regularSpecial = 'RegularSpecial',
    multi = 'Multi'
}

export enum SegmentFlags {
    nightTransfer = 'NightTransfer',
    changeAeroport = 'ChangeAeroport',
    longLayover = 'LongLayover'
}

export interface FlightModifyRules {
    isAllowable: boolean;
    fee: number;
}

export interface FlightSearchExternalOffer {
    billingPrice: number;
    externalFlightId: string;
    externalSearchId: string;
    partnerId: number;
    partnerName: string;
}

export interface FlightSearchIdRequest extends ApiRequest {
    searchId: string;
}

export interface FlightSearchPartner {
    id: number;
    name: string;
}

export interface FlightSearchRequest extends ApiRequest {
    sections: FlightSearchSection[];
    adults: number;
    children: number;
    babies: number;
    bookingClass: string;
    isRangeSearch: boolean;
    partner?: string; // = cookie g2sP
    partnerCookie: string; // = cookie g2sP
}

export interface FlightSearchResult extends FlightDictionaries {
    searchId: string;
    flights: Flight[];
    banners: Banner[];
}

export interface FlightSearchArchiveResult extends FlightSearchResult {
    searchRequest: FlightSearchRequest;
}

export interface FlightSearchSection {
    to: string;
    from: string;
    date: string;
}

export enum FlightStopKind {
    Standard,
    ByPassenger
}

export interface FlightTermsRequest extends ApiRequest {
    searchId: string;
    flightId: string;
}

export interface GetFlightBookingDetailsRequest extends ApiRequest {
    baggage: string; // for url query param stats
    clientDevice: ClientDevice;
    flightId: string;
    gaClientId: string;
    partnerCookie: string;
    searchId: string;
    skipCheckBooking: boolean;
    markerReferrer: string | null;
    queryString: string;
}

export interface GetReservationPublicIdRequest {
    reservationNumber: string;
    emailOrPhone: string;
}

export interface GetReservationPublicIdResponse {
    reservationPublicId: string;
}

export interface GetReservationStatusResult extends FlightDictionaries {
    reservationInfo: ReservationInfo;
    flight: Flight;
    billInfo: BillInfo;
    paymentTypes: PaymentTypeDesc[];
    changeRefundFee: number;
    changeRefundFeeCurrency: string;
    isMaskedPersonalData?: boolean;
    redirectUrl: string | null;
}

interface RetailRocket {
    sessionId: string;
    timestamp: string;
}

export interface RRUserAuthenticated {
    userId?: number;
}

export interface RRFlightDetailsOpened {
    flightId: string;
    searchId: string;
}

export interface RROrderFormOpened {
    flightId: string;
    searchId: string;
    products: Product[];
    mailTrackingId?: string;
}

export interface RROrderFormStepOpened {
    stepNumber: number;
    flightKey: string;
    products: Product[];
    bookingSessionId: number;
    email: string;
}

export interface RRProductSelected {
    productId: number;
    flightKey: string;
    bookingSessionId?: number;
}

export interface RRReservationCreated {
    reservationId: number;
    flightKey: string;
    products: Product[];
}

export interface RRSearchCompleted {
    searchId: string;
    searchPhrase: string;
    mailTrackingId?: string;
}

export interface HyggeDetailsModel {
    id: string;
    hyggeId: string;
    details: string;
    paid: boolean;
    airlineCode: string;
    airlineName: string;
}

export interface HyggeModel {
    id: string;
    name: string;
}

export interface HyggeResult {
    hyggies: { [id: string]: HyggeModel[] };
    items: HyggeDetailsModel[];
}

export interface InsurancePolicy {
    id: number;
    externalId: string;
    productId: number;
    providerId: string;
    url: string;
}

export interface Leg {
    baggageBySegments: boolean;
    bookingClass: string;
    flightKind: FlightKind;
    groupingId: string;
    segments: Segment[];
}

export interface Seat {
    available: boolean;
    availableChild: string;
    column: number;
    currency: string;
    id: string;
    letter: string;
    number: string;
    price: number;
    row: number;
    type: number | null;
    withInfant: boolean;
    selected: boolean;
}

export interface SeatMap {
    columns: number;
    flightCode: string;
    rows: any;
    rowsCount: number;
    seatBrands: SeatBrand[];
    segmentFlightCode: string;
}

export interface MarkerOwner {
    id: number;
    value: string;
}

export interface NextTripPurpose {
    id: string;
    name: string;
}

export interface PassengersCreateData {
    contactEmail: string;
    contactPhone: string;
    passengers: PassengerCreateData[];
}

export interface PassengerCreateData {
    firstName: string;
    middleName: string;
    lastName: string;
    birthday: string;
    gender: string;
    card: string;
    cardAirlineId: string;
    chosenSeatId: string[] | null;
    citizenship: number;
    documentNumber: string;
    documentValidTill: string;
    baggageOptions: string[];
    trainPlaces?: TrainPlace[];
    type: PassengerType | null;
}

export interface PassengerInfo {
    passengerId: number;
    firstName: string;
    middleName: string;
    lastName: string;
    birthday: string; // string used because of timezone problems with Date
    gender: string;
    card: string;
    citizenship: string;
    documentNumber: string;
    documentValidTill: string; // string used because of timezone problems with Date
    ticketNumber: string;
    insurances: InsurancePolicy[];
}

export enum PassengerType {
    adult = 'adult',
    child = 'child',
    infant = 'infant'
}

export interface PaymentInfo {
    redirect: PaymentInfoRedirect;
    inPlace: PaymentInfoInPlace;
    billPublicId: string;
    paymentId: number;
    errorCode?: number;
    split?: {
        agencyAmount: number;
        agencyAmountCurrency: string;
        airlineAmount: number;
        airlineAmountCurrency: string;
    };
}

export interface PaymentInfoRedirect {
    requestParameters: RequestParam[];
    url: string;
    method: string;
}

export interface PaymentInfoInPlace {
    succeed: boolean;
    status: string;
    errorMessage: string;
    exceptionMessage?: string;
}

export interface PaymentTypeDesc {
    paymentTypeId: string;
    price: number;
    displayPrice?: number;
    commission: number;
    template: string;
    kind: string;
    name: string;
    farePrice: number;
    taxes: number;
    serviceFee: number;
    discount: number;
    basePrice: number;
    insurancePackagePrice: number;
    servicePackagePrice: number;
    customBaggageCost: number;
    paymentMethods?: any;
    payed: number;
    isDirectPayment: boolean;
    paymentTypeOptions: any;
}

export interface PayReservationRequest extends ApiRequest {
    billPublicId?: string;
    bonusAmount?: number;
    clientDevice: ClientDevice;
    confirmationPageUrlTemplate: string;
    creditCard?: CreditCard;
    paymentTypeId: string;
    reservationPublicId: string;
}

export interface Price {
    value: number;
    currency: string;
}

export interface Product {
    acceptOrDenyRequired: boolean;
    adapter: string;
    additionalProcessing?: boolean | null;
    backgroundColor?: string;
    billingPrice: number;
    buttonAccept: string;
    cancelPossible: boolean;
    cancelUnavailabilityReason?: string | null;
    currencyCode?: string;
    couponDiscountAmount?: number;
    description: string;
    descriptionConfirm: string;
    descriptionShort: string;
    details: any;
    discountRuleId: number;
    displayType: string;
    externalId: string;
    hint: any;
    id: number;
    kitId?: number;
    labelAccept: string;
    labelDeny: string;
    name: string;
    price: number;
    priceCurrency: number;
    priceType: string;
    priority: number;
    productId: number;
    productReservationId: number;
    saleType: ProductSaleType;
    selectedByDefault: boolean;
    status: ProductStatus;
    stepNumber: number;
    type: string;
    sortOrder: number;
    flightId?: any;
    internalProducts?: any;
    internalProductIds?: any;
    crossedOutPrice?: number | null;
    timerText?: string | null;
    timerDuration?: number | null;
    tagText: string | null;
    tagColor: string | null;
    prefixText: string | null;
    contents?: string[];
}

export interface ProductPopup {
    backgroundColor: number;
    brandedKitId: number | null;
    buttonAction1: string;
    buttonAction2: string;
    buttonAction3: string;
    buttonText1: string;
    buttonText2: string;
    buttonText3: string;
    buttonColor1: string;
    buttonColor2: string;
    buttonColor3: string;
    buttonIcon1?: string;
    buttonIcon2?: string;
    buttonIcon3?: string;
    buttonStyle1: string;
    buttonStyle2: string;
    buttonStyle3: string;
    discountId?: number;
    displayType?: any;
    emTextColor?: string;
    isCloseIconVisible: boolean;
    kitId: number;
    markerText: string;
    popupId: number;
    position: ProductPopupPosition;
    productId: number;
    productName?: string | null;
    reverse?: boolean;
    reservationPriceText?: string | null;
    secondProductId?: number;
    targetProductId: number;
    text: string;
    type: ProductPopupType;
    popupTypeNumber: number;
    firstButtonText?: string;
    crossedOutPrice?: number | null;
    timerText?: string | null;
    timerDuration?: number | null;
}

export interface ProductBrand {
    acceptOrDenyRequired: boolean;
    contents: [];
    kitId: number;
    kitTarget: string;
    products: Product[];
    stepOnForm: number;
    topText: string;
    style: string;
    needToSelect: boolean;
}
export interface ProductBranded {
    backgroundColor: string;
    buttonAcceptColor: string;
    buttonAcceptStyle: string;
    buttonAcceptText: string;
    contents: any;
    contentsColor: string;
    displayType: string;
    hint?: any;
    hintColor: string;
    kitId: number;
    priceTextColor: string;
    productId: number;
    productName: string;
    sortOrder: number;
    type: string;
}

export interface BrandedProductKits {
    contents: string[];
    kitId: number;
    kitTarget: string;
    products: ProductBranded[];
    stepOnForm: number;
    style: string;
    subText: string;
    topText: string;
}

export interface CancelProductResponse {
    message: string;
    localizedMessage: string;
    success: boolean;
    productReservationId: number;
    popup: ProductPopup;
    product: Product;
}

export enum ProductPopupPosition {
    afterOrder = 'AfterOrder',
    afterStep1 = 'AfterStep1',
    afterStep2 = 'AfterStep2'
}

export enum ProductPopupType {
    product = 'Product',
    flightBaggage = 'FlightBaggage',
    discount = 'Discount',
    gift = 'Gift'
}

export enum ProductSaleType {
    bookingForm = 'BookingForm',
    popup = 'Popup',
    yandex = 'YandexPayButton'
}

export enum ProductStatus {
    /** Забронирован. */
    booked = 'Booked',
    /** Выписан. */
    issued = 'Issued',
    /** Возвращен */
    refunded = 'Refunded',
    /** Отменен */
    canceled = 'Canceled',
    /** Удален. (Храним PR в базе, но в коде отсекаем такие PR, чтобы не поехали отчеты и т.п) */
    deleted = 'Deleted'
}

export interface ProductBookingDetails {
    paymentTypes: PaymentTypeDesc[];
    products: Product[];
    reservationInfo: ReservationInfo;
    redirectUrl: string | null;
}

export interface ProductBookingDetailsRequest extends ApiRequest {
    reservationPublicId: string;
    productId?: number;
    productIds?: number[];
    choiceType?: ProductChoiceType;
    redirectDomain?: string;
}

export interface ProductBookingInfo {
    id: number;
    options: string[];
    choiceType: ProductChoiceType;
}

export enum ProductChoiceType {
    // Значение по-умолчанию.
    notSet = 0,

    // Выбран вручную (на ФЗ).
    manualAccepted = 1,

    // Отказался вручную (на ФЗ).
    manualDenied = 2,

    // Согласился в догонялке.
    confirmationAccepted = 3,

    // Отказался в догонялке.
    confirmationDenied = 4,

    // Согласился в догонялке к догонялке (ДКД).
    popupForPopupAccepted = 5,

    // Отказался в догонялке к догонялке (ДКД).
    popupForPopupDenied = 6,

    // Согласился на странице заказа.
    orderPageAccepted = 7,

    // Отказался на странице заказа.
    orderPageDenied = 8,

    // Согласился на странице заказа, но перешел на нее из бота в телеграмме.
    telegramBotAccepted = 9,

    // Отказался на странице заказа, но перешел на нее из бота в телеграмме.
    telegramBotDenied = 10,

    // Согласился в транзакционном (допродажном) письме.
    transactionEmailAccepted = 11,

    // Отказался в транзакционном (допродажном) письме.
    transactionEmailDenied = 12,

    // Согласился в системном (например письмо с билетом) письме.
    systemEmailAccepted = 13,

    // Отказался в системном (например письмо с билетом) письме.
    systemEmailDenied = 14,

    // Согласился и оформил услугу через "Службу Заботы".
    customerCareServiceAccepted = 15,

    // Отказался и оформил услугу через "Службу Заботы".
    customerCareServiceDenied = 16,

    // Согласился в письме отправленным компанией RetailRocket.
    retailEmailAccepted = 17,

    // Отказался в письме отправленным компанией RetailRocket.
    retailEmailDenied = 18,

    // Согласился в догонялке на ФЗ.
    popupOnOrderFormAccepted = 19,

    // Отказался в догонялке на ФЗ.
    popupOnOrderFormDenied = 20,

    // Услуга выбрана через i-api b2b Aviasales.
    integrationApiAccepted = 21,

    // Услуга отказана через i-api b2b Aviasales.
    integrationApiDenied = 22,
}

export enum ProductType {
    insurance = 'Insurance',
    service = 'Service',
    package = 'Package',
}

export enum ProductDisplayType {
    grouped =  'Grouped'
}

export interface PromotionMemberCreateRequest extends ApiRequest {
    promotionId: number;
    email: string;
    name: string;
    phone: string;
}

export interface PromotionMemberCreateResult {
    success: boolean;
    user: User;
}

export interface RequestParam {
    paramName: string;
    paramValue: string;
}

export enum NameConstraint {
    notAllowed = 'NotAllowed',
    required = 'Required',
    optional = 'Optional',
}

export interface ReservationDocumentsConstrains {
    nameProperty: string;
    nameConstraint: NameConstraint;
    typeConstraint: number;
}

export interface ReservationDocuments {
    nameDocument: string;
    typeIdentityDocument: string;
    constraints: ReservationDocumentsConstrains;
}

export interface ConstraintsReasonText {
    reason: string;
    text: string;
}

export interface CitizenshipConstraint {
    airlineId: number;
    countryId: number;
    reason: string;
    type: string;
}

export interface SurchargeInfo {
    buttonAcceptText: string;
    buttonRejectText: string;
    clientText: string;
    surchargeAmount: number;
    surchargeAmountCurrency: string;
    type: string;
}

export interface ReservationConstraints {
    allowCyrillicDocumentNumber: boolean;
    allowCyrillicPassengerName: boolean;
    citizenshipConstraints: CitizenshipConstraint[];
    documentValidTillRequired: boolean;
    documentValidTillVisible: boolean;
    documents?: any[];
    documentPassportForeign?: ReservationDocuments | null;
    documentPassportRu?: ReservationDocuments | null;
    documentBirthCertificate?: ReservationDocuments | null;
    documentOtherDocument?: ReservationDocuments | null;
    orderFormNewDocumentNumberRuValidationEnabled: boolean;
    lastFlightDate: string;
    maxAdultDateOfBirth: string;
    maxChildDateOfBirth: string;
    maxInfantDateOfBirth: string;
    middleNameRequired: boolean;
    mileCardVisible: boolean;
    minAdultDateOfBirth: string;
    minChildDateOfBirth: string;
    minInfantDateOfBirth: string;
    reasonText: ConstraintsReasonText[];
    requiredCitizenshipCountries: number[];
}

export interface ReservationInfo {
    amount: number;
    bookingClass: string;
    contactEmail: string;
    contactPhone: string;
    couponDiscountAmount: number;
    createdDate: string;
    currencyCode?: string;
    customBaggage: CustomBaggageReservationDetails;
    discountOrFee?: number;
    isChangeRefundPossible: boolean;
    isPayed: boolean;
    isPaymentPossible: boolean;
    autoVoidPossible: boolean;
    isStatusPending: boolean;
    isTicketed: boolean;
    markerOwner: MarkerOwner;
    offer: number;
    passengers: PassengerInfo[];
    pnr: string;
    products: Product[];
    productsOnOrderPage: {
        grouping: string;
        products: Product[];
    };
    parentReservationPublicId: string;
    reservationNumber: string;
    reservationPublicId: string;
    reservationStatus: string;
    reservedTill: string;
    reservedTillExpired: boolean;
    surchargeInfo: SurchargeInfo;
    uiStatus: string;
    uiStatusDetails: string;
    uiStatusDetailsType: string;
    uiStatusType: string;
}

export interface AutovoidSupportText {
    adviseText: string;
    accountText: string;
}

export interface AutovoidStartedText {
    headerText: string;
    messageText: string;
    buttonAcceptText: string;
}

export interface CheckAutoVoidAvailable {
    isAutoVoidAvailable: boolean;
}

export interface OrdersExists {
    orderExists?: boolean;
    message?: string;
}

export interface RefundInfo {
    clientText: string;
    headerText: string;
    youPaidText: string;
    weWillRefundText: string;
    termText: string;
    buttonAcceptText: string;
    buttonRejectText: string;
    buttonWriteManuallyText?: string;
    refundSum: number;
    paidSum: number;
    refundSumCurrency: string;
}

export interface ReservationListModel {
    id: number;
    publicId: string;
    userId: number;
    statusId: number;
    reservationDate: string;
    lastModifiedDate: string;
}

export interface ReservationStatusResult extends FlightDictionaries {
    reservationInfo: ReservationInfo;
    flight: Flight;
    billInfo: BillInfo;
    paymentTypes: PaymentTypeDesc[];
    changeRefundFee: number;
    changeRefundFeeCurrency: string;
}

export interface SaveReservationOpinionRequest {
    reservationPublicId: string;
    opinion: number;
}

export interface Segment {
    aircraftUID: string;
    airlineUID: string; // Validating airline
    arrivalDate: string;
    arrivalDateLocal: string;
    arrivalDestinationUID: string;
    arrivalTerminal: string;
    baggage: FlightBaggage;
    baggageCabin: FlightBaggage;
    departureDate: string;
    departureDateLocal: string;
    departureDestinationUID: string;
    departureTerminal: string;
    exchangeRules: FlightModifyRules;
    segmentFlags: SegmentFlags[];
    flightCode: string;
    nextSegmentStop: FlightStopKind;
    operatingAirlineUID: string; // Operating airline
    refundRules: FlightModifyRules;
    stops: SegmentStop[];
    supplierPnr: string;
    transport: SegmentTransport;
}

export interface SegmentStop {
    aircraftUID: string;
    arrivalDate: string;
    arrivalDateLocal: string;
    departureDate: string;
    departureDateLocal: string;
    destinationUID: string;
}

export enum SegmentTransport {
    air = 'Air',
    bus = 'Bus',
    train = 'Train'
}

export interface SupportContacts {
    skype: string;
    phones: string[];
    workingHours: SupportWorkingHours;
    weekendWorkingHours: SupportWorkingHours;
}

export interface SupportCreateRequest extends ApiRequest {
    orderNumber: string;
    email: string;
    category: string;
    description: string;
}

export interface SupportCreateResult {
    success: boolean;
    message: string;
}

export interface ReservationSupportInfo {
    reservationId: number;
    reservationCreateDate: string;
    reservationInfo: string;
    reservationStatus: string;
    ticketStatus: string;
    ticketExist: boolean;
}

export interface SupportGetReservationsSupportInformationResult {
    reservationsSupportInfo: ReservationSupportInfo[];
}

export interface SupportTicketComment {
    createdAt: string;
    author: string;
    message: string;
}

export interface SupportTicket {
    reservationId: number;
    status: string;
    topic: string;
    comments: SupportTicketComment[];
}

export interface SupportGetHistoryResult {
    tickets: SupportTicket[];
}

export interface SupportWorkingHours {
    from: string;
    to: string;
}

export interface SbpRefundBank {
    id: number;
    name: string;
}

export interface SbpRefundInfo {
    refundSum: number;
    currency: string;
    banks: SbpRefundBank[];
    message: string;
}

export interface SubmitSbpRefundRequest {
    publicId: string;
    viewedFrom: number; // 0 - Unknown, 1 - Sms, 2 - Email
    phoneNumber: string;
    refundBankId: number;
    clientIp: string;
    refundBankName: string;
}

export interface Train {
    number: string;
    name: string | null;
    provider: string;
    isSuburban: boolean;
    direction: 'Outward' | 'Return' | 'Both';
    coaches: Coach[];
}

export interface TrainPlace {
    train: string;
    coach: string;
    place: string;
    placeType: string;
    gender: string;
}

export interface SeatBrand {
    description: string;
    id: number;
    title: string;
}

export interface UpdateBookingSessionRequest extends ApiRequest {
    bookingSessionId: number;
    email: string;
    phone: string;
}

export interface UpdateBookingSessionProductPopupRequest extends ApiRequest {
    bookingSessionId: number;
    productPopupId: number;
    popupType: number;
    isPopupOnForm: boolean;
}

export interface UpdateBookingSessionNotEnoughMoneyPopupRequest extends ApiRequest {
    bookingSessionId?: number;
    reservationPublicId?: string;
}

export interface Subscription {
    id: number;
    departureDestination: string;
    arrivalDestination: string;
    dateOutward: Date;
    dateReturn: Date | null;
}

export interface BestPriceGetSubscriptionsRequest {
    email: string;
}

export interface BestPriceGetSubscriptionsResult {
    subscriptions: Subscription[];
}

export interface BestPriceSubscribeRequest {
    searchId: string;
    email: string;
    onlyFreeBaggage: boolean;
    onlyDirectFlight: boolean;
}

export interface BestPriceSubscribeResult {
    subscriptionId: number;
}

export interface BestPriceUnsubscribeRequest {
    email: string;
    subscriptionIds: number[];
}

export interface ActiveReservationPublicId {
    id: string | null;
}

export interface User {
    city: Destination | null;
    country: Destination | null;
    email: string | null;
    firstName: string;
    lastName: string;
    phone: string | null;
    birthday: string; // string used because of timezone problems with Date
    gender: string;
    hasPassword: boolean;
    nextTripPurposes: string[];
    roles: number | null;
    resetMarker?: boolean;
}

export interface UserAccount extends User {
    bonuses: UserBonuses;
    companyAccount: CorporateAccount;
    countries: Destination[];
    cities: Destination[];
    allNextTripPurposes: NextTripPurpose[];
    orderChangeVisible: boolean;
    orderReturnVisible: boolean;
    passwordChangeVisible: boolean;
}

export interface UserBonuses {
    enabled: boolean;
    status: UserBonusStatus;
    bonusMaxLimitPerOrder: number;
    bonusMaxPercentLimitPerOrder: number;
    reservationMinPrice: number;
}

export interface UserBonusStatus {
    creditTotal: number;
    debitTotal: number;
    currentBalance: number;
    canBeUsed: boolean;
    leftToFirstUse: number;
    transfers: UserBonusTransfer[];
}

export interface UserBonusTransfer {
    id: number;
    userId: number;
    bonusAmount: number;
    transferType: string;
    transferReason: string;
    date: string;
    reservation: ReservationListModel;
}

export interface UserWithBonuses extends User {
    bonuses: UserBonuses;
    companyAccount: CorporateAccount;
}

export interface UserWithProfile extends User {
    countries: Destination[];
    cities: Destination[];
    allNextTripPurposes: NextTripPurpose[];
}
